import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import Modal from '@/components/base/Modal';
import type { CashierProfile } from '@/lib/types';

interface CashierFormState {
  first_name: string;
  last_name: string;
  phone: string;
  branch_id: string;
  active: boolean;
  custom_code: string;
}

interface Credentials {
  email: string;
  access_code: string;
  title: string;
}

const emptyForm: CashierFormState = {
  first_name: '',
  last_name: '',
  phone: '',
  branch_id: '',
  active: true,
  custom_code: '',
};

async function invokeCashier(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke('manage_cashier', { body });
  if (error) {
    let msg = error.message;
    const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
    if (ctx?.json) {
      try {
        const j = await ctx.json();
        if (j?.error) msg = j.error;
      } catch {
        // ignore
      }
    }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error as string);
  return data as Record<string, unknown>;
}

export default function CashierSection() {
  const { branches, activeBranchId } = useBranch();
  const [cashiers, setCashiers] = useState<CashierProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CashierProfile | null>(null);
  const [form, setForm] = useState<CashierFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [regenTarget, setRegenTarget] = useState<CashierProfile | null>(null);
  const [regenCode, setRegenCode] = useState('');
  const [regenError, setRegenError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'cashier')
        .order('first_name');
      if (e) throw e;
      setCashiers((data as CashierProfile[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los cajeros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, branch_id: activeBranchId ?? branches[0]?.id ?? '' });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (c: CashierProfile) => {
    setEditing(c);
    setForm({
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      phone: c.phone ?? '',
      branch_id: c.branch_id ?? '',
      active: !c.blocked,
      custom_code: '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSave = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    setFormError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setFormError('Nombre y apellido son obligatorios.');
      return;
    }
    if (!form.branch_id) {
      setFormError('Selecciona una sucursal.');
      return;
    }
    if (!editing && !form.custom_code.trim()) {
      setFormError('El código de acceso es obligatorio.');
      return;
    }
    if (form.custom_code.trim() && !/^\d{6}$/.test(form.custom_code.trim())) {
      setFormError('El código de acceso debe tener exactamente 6 dígitos.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await invokeCashier({
          action: 'update',
          user_id: editing.id,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          branch_id: form.branch_id,
          phone: form.phone.trim() || null,
          active: form.active,
        });
        setModalOpen(false);
        load();
      } else {
        const res = await invokeCashier({
          action: 'create',
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          branch_id: form.branch_id,
          phone: form.phone.trim() || null,
          active: form.active,
          custom_code: form.custom_code.trim(),
        });
        setModalOpen(false);
        setCredentials({
          email: (res.email as string) ?? '',
          access_code: (res.access_code as string) ?? '',
          title: 'Cajero creado correctamente',
        });
        load();
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar el cajero');
    } finally {
      setSaving(false);
    }
  };

  const openRegenerate = (c: CashierProfile) => {
    setRegenTarget(c);
    setRegenCode('');
    setRegenError(null);
  };

  const handleRegenerateConfirm = async () => {
    if (!regenTarget) return;
    const trimmed = regenCode.trim();
    if (trimmed && !/^\d{6}$/.test(trimmed)) {
      setRegenError('El código debe tener exactamente 6 dígitos.');
      return;
    }
    setSaving(true);
    setRegenError(null);
    try {
      const res = await invokeCashier({
        action: 'regenerate_code',
        user_id: regenTarget.id,
        ...(trimmed ? { custom_code: trimmed } : {}),
      });
      setCredentials({
        email: regenTarget.email ?? '',
        access_code: (res.access_code as string) ?? '',
        title: 'Nuevo código de acceso generado',
      });
      setRegenTarget(null);
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : 'No se pudo generar el código');
    } finally {
      setSaving(false);
    }
  };

  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  };

  const branchName = (id: string | null) => branches.find((b) => b.id === id)?.name ?? '—';

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div>
      <div className="flex justify-end">
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-user-add-line" /> Crear cajero
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : cashiers.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-team-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay cajeros registrados.</p>
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-background-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Cajero</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Correo interno</th>
                <th className="px-4 py-3 font-medium">Sucursal</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {cashiers.map((c) => (
                <tr key={c.id} className="hover:bg-background-100 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center font-semibold shrink-0">
                        {((c.first_name || '?')[0] || '?').toUpperCase()}
                      </div>
                      <span className="font-medium text-foreground-950">
                        {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 hidden md:table-cell">
                    <span className="text-xs">{c.email || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{branchName(c.branch_id)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.blocked ? 'bg-secondary-100 text-secondary-900' : 'bg-accent-100 text-accent-900'
                      }`}
                    >
                      {c.blocked ? 'Inactivo' : 'Activo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-primary-700 hover:bg-primary-50 cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => openRegenerate(c)}
                        disabled={saving}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer disabled:opacity-50"
                        title="Generar nuevo código"
                      >
                        <i className="ri-refresh-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Crear / Editar cajero */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar cajero' : 'Crear cajero'}
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : editing ? 'Guardar' : 'Crear cajero'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          {!editing && (
            <div className="flex items-start gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-700">
              <i className="ri-information-line mt-0.5 text-primary-700" />
              <span>
                El código de acceso es la clave con la que el cajero ingresará al sistema. El correo
                interno se genera automáticamente.
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nombre *</label>
              <input
                value={form.first_name}
                onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                placeholder="Carlos"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Apellido *</label>
              <input
                value={form.last_name}
                onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                placeholder="Pérez"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Teléfono</label>
            <input
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="809-000-0000"
              className={inputClass}
            />
          </div>
          {!editing && (
            <div>
              <label className={labelClass}>Código de acceso *</label>
              <input
                value={form.custom_code}
                onChange={(e) => setForm((p) => ({ ...p, custom_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="6 dígitos"
                inputMode="numeric"
                maxLength={6}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-foreground-500">
                Este código es obligatorio y será el método de acceso del cajero.
              </p>
            </div>
          )}
          <div>
            <label className={labelClass}>Sucursal asignada *</label>
            <select
              value={form.branch_id}
              onChange={(e) => setForm((p) => ({ ...p, branch_id: e.target.value }))}
              className={inputClass}
            >
              <option value="">Selecciona una sucursal</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          {editing && (
            <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
              <span className="text-sm font-medium text-foreground-900">Cuenta activa</span>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                className="w-5 h-5 accent-primary-500 cursor-pointer"
              />
            </label>
          )}
          {formError && (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
              {formError}
            </div>
          )}
        </form>
      </Modal>

      {/* Credenciales generadas */}
      <Modal
        open={!!credentials}
        onClose={() => setCredentials(null)}
        title={credentials?.title ?? 'Credenciales del cajero'}
        footer={
          <button
            onClick={() => setCredentials(null)}
            className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            Entendido
          </button>
        }
      >
        {credentials && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
              <i className="ri-error-warning-line mt-0.5" />
              <span>
                Guarda estas credenciales ahora. Por seguridad, el código no se volverá a mostrar.
              </span>
            </div>

            <div className="rounded-md border border-background-200 p-4">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide">
                Correo interno
              </p>
              <div className="mt-1 flex items-center justify-between gap-3">
                <span className="text-sm text-foreground-900 break-all">{credentials.email}</span>
                <button
                  onClick={() => copy(credentials.email, 'email')}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 shrink-0 cursor-pointer"
                  title="Copiar"
                >
                  <i className={copied === 'email' ? 'ri-check-line text-accent-600' : 'ri-file-copy-line'} />
                </button>
              </div>
            </div>

            <div className="rounded-md border border-primary-200 bg-primary-50 p-4 text-center">
              <p className="text-xs font-medium text-primary-700 uppercase tracking-wide">
                Código de acceso
              </p>
              <p className="mt-2 font-heading text-4xl font-bold tracking-[0.4em] text-foreground-950">
                {credentials.access_code}
              </p>
              <button
                onClick={() => copy(credentials.access_code, 'code')}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline cursor-pointer"
              >
                <i className={copied === 'code' ? 'ri-check-line' : 'ri-file-copy-line'} />
                {copied === 'code' ? 'Copiado' : 'Copiar código'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Regenerar código */}
      <Modal
        open={!!regenTarget}
        onClose={() => setRegenTarget(null)}
        title="Generar nuevo código"
        footer={
          <>
            <button
              onClick={() => setRegenTarget(null)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleRegenerateConfirm}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Generando...' : 'Generar código'}
            </button>
          </>
        }
      >
        {regenTarget && (
          <div className="space-y-4">
            <p className="text-sm text-foreground-700">
              Vas a generar un nuevo código de acceso para{' '}
              <span className="font-medium text-foreground-950">
                {[regenTarget.first_name, regenTarget.last_name].filter(Boolean).join(' ')}
              </span>
              . El código anterior dejará de funcionar.
            </p>
            <div>
              <label className={labelClass}>Código personalizado (opcional)</label>
              <input
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6 dígitos (se genera automáticamente)"
                inputMode="numeric"
                maxLength={6}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-foreground-500">
                Si lo dejas vacío, el sistema genera uno automáticamente.
              </p>
            </div>
            {regenError && (
              <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
                {regenError}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}